import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { useAuth } from "../../lib/auth";
import { projectApi, roleLabel, errText, FOUNDER } from "../../lib/projectAccess";

// People & Access → user detail: the user's project assignments, plus (Founder only, PM users) the
// "Company-wide project visibility (view only)" grant. Both changes are audited server-side.
export default function UserProjectAssignments({ userId, targetRole }) {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const isFounder = me?.role_key === FOUNDER;

  useEffect(() => {
    projectApi.userAssignments(userId).then((r) => setRows(r.data)).catch(() => setRows([]));
    if (isFounder && targetRole === "PROJECT_MANAGER") {
      projectApi.companyView(userId).then((r) => setView(r.data)).catch(() => setView(null));
    }
  }, [userId, targetRole, isFounder]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      await projectApi.setCompanyView(userId, on);
      setView((v) => ({ ...v, company_wide_view: on }));
      toast.success(on ? "Company-wide view granted (view only)" : "Company-wide view removed");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const live = (rows || []).filter((r) => !r.removed_at);
  return (
    <Card className="md:col-span-2 border-erf-borderCream bg-erf-cream" data-testid="user-detail-project-assignments">
      <CardContent className="p-5 space-y-3">
        <h2 className="font-nav text-sm font-semibold text-erf-dark">Project assignments</h2>
        {view?.eligible && (
          <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-erf-borderCream rounded p-3">
            <label htmlFor="pm-company-view" className="text-sm font-nav text-erf-dark">
              Company-wide project visibility (view only)
              <span className="block text-[11px] text-erf-dark/60">Lets this PM view every project. It adds no edit, financial or admin rights.</span>
            </label>
            <Switch id="pm-company-view" checked={!!view.company_wide_view} disabled={busy} onCheckedChange={toggle}
              className="min-w-[44px]" data-testid="user-detail-pm-company-view-toggle" />
          </div>
        )}
        {rows === null && <div className="font-mono text-xs text-erf-dark/60">Loading…</div>}
        {rows && live.length === 0 && <p className="text-xs text-erf-dark/70" data-testid="user-detail-assignments-empty">Not assigned to any project.</p>}
        <ul className="grid gap-2" data-testid="user-detail-assignments-list">
          {live.map((a) => (
            <li key={a.id} className="flex flex-wrap justify-between gap-2 bg-white border border-erf-borderCream rounded p-3 text-sm">
              <Link to={`/projects/${a.project_id}?tab=team`} className="underline min-h-[44px] inline-flex items-center"
                data-testid={`user-detail-assignment-${a.project_id}`}>Project {a.project_id.slice(0, 8)}</Link>
              <span className="text-xs text-erf-dark/70 self-center">{roleLabel(a.role_on_project)} · since {(a.assigned_at || "").slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
